import { TestingGroup } from '@kcftech/prometheus-client';
import { signIn, signOut } from 'auth-provider';
import { sdClient } from 'contexts/SDKClientContext';
import { useEffect, useState } from 'react';

const STASH_KEY = 'qa_stashed_admin_session';
const COGNITO_PREFIX = 'CognitoIdentityServiceProvider.';

function getCognitoKeys(): string[] {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(COGNITO_PREFIX)) {
            keys.push(key);
        }
    }
    return keys;
}

function snapshotCognitoKeys(): Record<string, string | null> {
    const snapshot: Record<string, string | null> = {};
    for (const key of getCognitoKeys()) {
        snapshot[key] = localStorage.getItem(key);
    }
    return snapshot;
}

function writeCognitoKeys(snapshot: Record<string, string | null>) {
    for (const key of getCognitoKeys()) {
        localStorage.removeItem(key);
    }
    for (const [key, value] of Object.entries(snapshot)) {
        if (value !== null) {
            localStorage.setItem(key, value);
        }
    }
}

/**
 * Manages a stashed copy of admin Cognito keys in localStorage,
 * allowing temporary session swaps for QA user impersonation.
 */
export const adminSessionStash = {
    exists(): boolean {
        return localStorage.getItem(STASH_KEY) !== null;
    },

    save() {
        localStorage.setItem(STASH_KEY, JSON.stringify(snapshotCognitoKeys()));
    },

    activate(): boolean {
        const raw = localStorage.getItem(STASH_KEY);
        if (!raw) {
            return false;
        }
        writeCognitoKeys(JSON.parse(raw) as Record<string, string>);
        return true;
    },

    clear() {
        localStorage.removeItem(STASH_KEY);
    },

    /**
     * Run a callback with stashed (admin) keys temporarily active,
     * restoring the previous keys afterward. No-op if no stash exists.
     */
    async withSession<T>(fn: () => Promise<T>): Promise<T> {
        if (!this.exists()) {
            return fn();
        }
        const previousKeys = snapshotCognitoKeys();
        this.activate();
        try {
            return await fn();
        } finally {
            writeCognitoKeys(previousKeys);
        }
    },
};

export async function listQaUsers(testingGroup: TestingGroup) {
    // technically this is succeptible to racing with other requests, which may
    // be made with the temporary auth
    return adminSessionStash.withSession(() =>
        sdClient.listQaUsersByTestingGroup({ userGroup: testingGroup }),
    );
}

export type QaUserSwapAutoSigninCreds = {
    username: string;
    password: string;
    restoreUrl: string;
};
export const qaUserSwapAutoSigninCredsKey = 'autosignin-creds';

export const qaUserSwapAutoSigninCreds = {
    useWatch: () => {
        const [isSwapping, setIsSwapping] = useState(!!qaUserSwapAutoSigninCreds.get());
        useEffect(() => {
            const handler = (event: StorageEvent) => {
                if (
                    event.storageArea === sessionStorage &&
                    event.key === qaUserSwapAutoSigninCredsKey
                ) {
                    if (event.newValue) {
                        setIsSwapping(true);
                    } else {
                        setIsSwapping(false);
                    }
                }
            };
            window.addEventListener('storage', handler);
            return () => {
                window.removeEventListener('storage', handler);
            };
        }, []);
        return isSwapping;
    },
    set: (username: string, password: string, restoreUrl: string) => {
        sessionStorage.setItem(
            qaUserSwapAutoSigninCredsKey,
            JSON.stringify({ username, password, restoreUrl }),
        );
    },
    get: (): QaUserSwapAutoSigninCreds | null => {
        return JSON.parse(sessionStorage.getItem(qaUserSwapAutoSigninCredsKey) || 'null');
    },
    clear: () => {
        sessionStorage.removeItem(qaUserSwapAutoSigninCredsKey);
    },
};

export async function qaUserSwapAutoSignin() {
    const creds = qaUserSwapAutoSigninCreds.get();
    qaUserSwapAutoSigninCreds.clear();
    if (!creds) {
        return;
    }
    try {
        await signIn(creds);
        window.location.href = creds.restoreUrl;
    } catch (e) {
        adminSessionStash.clear();
        window.location.reload();
        throw e;
    }
}

export async function loginAsQaUserByEmail(testingGroup: TestingGroup, email: string) {
    const isCurrentlyImpersonating = adminSessionStash.exists();

    if (isCurrentlyImpersonating) {
        // temporarily activate the admin session so we have permissions for the
        // getQaUserPassword call below
        adminSessionStash.activate();
    } else {
        // save admin session prior to switching from it
        adminSessionStash.save();
    }

    try {
        const res = await sdClient.getQaUserPassword({
            userGroup: testingGroup,
            userEmail: email,
        });
        if (!res.Secret) {
            throw new Error('No password returned for user');
        }
        // signout changes the url and causes a full page reload. We'll need to
        // store a token to be autoloaded via `qaUserSwapAutoSigninCreds`
        // instead of chaining fn calls.
        qaUserSwapAutoSigninCreds.set(email, res.Secret, window.location.href);
        await signOut();
    } catch (err) {
        qaUserSwapAutoSigninCreds.clear();
        if (!isCurrentlyImpersonating) {
            adminSessionStash.activate();
            adminSessionStash.clear();
        }
        throw err;
    }
}

export function returnToAdminSession() {
    if (adminSessionStash.activate()) {
        adminSessionStash.clear();
        window.location.reload();
    }
}
